import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TaskListItem } from '@/components/TaskListItem';
import { useFilteredTasks, useSortedTasks } from '@/hooks/useFilteredTasks';
import { Button } from "@/components/ui/button";

import { ChevronUp, ChevronRight, ChevronDown, Plus, Calendar, MoreVertical, Edit, Trash2, Archive, MoveVertical, MoreHorizontal, X, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";
import { FiPackage } from 'react-icons/fi';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast"
import { QuickAddTask } from '@/components/QuickAddTask';
import { TaskPlaceholderItem } from '@/components/TaskPlaceholderItem';
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from './ui/scroll-area';
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { getReleaseStatusColor, STATUS_COLORS } from "@/lib/colorsMap";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

function getWeekNumber(d) {
    // Copy the date to avoid modifying the original
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    // Thursday in current week decides the year
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    // January 4 is always in week 1
    const week1 = new Date(date.getFullYear(), 0, 4);
    // Adjust to Thursday in week 1 and count number of weeks from date to week1
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

export function ProjectList({ isLoading, project, filterByAssignee = new Set(), filterBy, user, setProject, sortBy, groupByAssignee, refetchProject, onShowReleaseDetails }) {
    if (isLoading || !project) {
        return (
            <div className="space-y-6 pb-6">
                {/* Skeleton for the "Load Previous Week" button */}
                <div className="flex justify-center">
                    <Skeleton className="h-8 w-40 rounded" />
                </div>
                {/* Skeleton for the Active Week Section */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                            {/* Left side week title skeleton */}
                            <Skeleton className="h-6 w-32" />
                            {/* Right side week range skeleton */}
                            <Skeleton className="h-4 w-40" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {[...Array(3)].map((_, index) => (
                                // Reduced height for list items from 80px to 60px
                                <Skeleton key={index} className="h-[60px] w-full" />
                            ))}
                        </div>
                    </CardContent>
                </Card>
                {/* Skeleton for the Backlog Section */}
                <Card>
                    <CardHeader>
                        <CardTitle>
                            <Skeleton className="h-6 w-32" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {[...Array(2)].map((_, index) => (
                                // Reduced height for list items from 80px to 60px
                                <Skeleton key={index} className="h-[60px] w-full" />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { axiosInstance } = useAuth();
    const { toast } = useToast()
    const [loadingPrevious, setLoadingPrevious] = useState(false);
    const [loadingNext, setLoadingNext] = useState(false);
    const [previousWeeks, setPreviousWeeks] = useState([]);
    const [selectedTasks, setSelectedTasks] = useState(new Set());
    const [isDraggingAny, setIsDraggingAny] = useState(false);
    const [showNewReleaseForm, setShowNewReleaseForm] = useState(false);
    const [newReleaseName, setNewReleaseName] = useState("");
    const [newReleaseTargetDate, setNewReleaseTargetDate] = useState(null);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    
    // Release activation dialog state
    const [showReleaseActivationDialog, setShowReleaseActivationDialog] = useState(false);
    const [pendingDragOperation, setPendingDragOperation] = useState(null);
    const [activationChoice, setActivationChoice] = useState("move-task-only");
    
    // Release menu activation dialog state
    const [showReleaseMenuActivationDialog, setShowReleaseMenuActivationDialog] = useState(false);
    const [releaseToActivate, setReleaseToActivate] = useState(null);
    const [isActivatingRelease, setIsActivatingRelease] = useState(false);
    
    // Release deactivation dialog state
    const [showReleaseDeactivationDialog, setShowReleaseDeactivationDialog] = useState(false);
    const [releaseToDeactivate, setReleaseToDeactivate] = useState(null);
    const [isDeactivatingRelease, setIsDeactivatingRelease] = useState(false);
    

    
    // Release deletion dialog state
    const [showReleaseDeleteDialog, setShowReleaseDeleteDialog] = useState(false);
    const [releaseToDelete, setReleaseToDelete] = useState(null);

    // Release ship dialog state
    const [showReleaseShipDialog, setShowReleaseShipDialog] = useState(false);
    const [releaseToShip, setReleaseToShip] = useState(null);
    const [isShippingRelease, setIsShippingRelease] = useState(false);

    // Release delete dialog state
    const [isDeletingRelease, setIsDeletingRelease] = useState(false);

    // Release details dialog state



    // Bulk operations state
    const [bulkMoveTarget, setBulkMoveTarget] = useState(null);

    // Calculate releases with proper status using useMemo for consistency
    const releases = useMemo(() => {
        const calculateReleaseStatus = (release) => {
            const now = new Date();
            
            // If actualReleaseDate is set, it's shipped
            if (release.actualReleaseDate) {
                return 'shipped';
            }
            
            // If no start date is set, it's just waiting to be started
            if (!release.startDate) {
                return 'upcoming';
            }
            
            const startDate = new Date(release.startDate);
            
            // If start date is in the future
            if (now < startDate) {
                return 'upcoming';
            }
            
            // If no target date is set, consider it active after start date
            if (!release.targetDate) {
                return now >= startDate ? 'active' : 'upcoming';
            }
            
            const targetDate = new Date(release.targetDate);
            
            // If we're between start and target date
            if (now >= startDate && now <= targetDate) {
                return 'active';
            }
            
            // If we're past the target date
            return 'overdue';
        };

        return (project?.releases || []).map(release => ({
            ...release,
            status: calculateReleaseStatus(release)
        }));
    }, [project?.releases]);

    // Create a lookup map for release status updates
    const releaseStatusMap = useMemo(() => {
        const map = new Map();
        releases.forEach(release => {
            map.set(release._id, release.status);
        });
        return map;
    }, [releases]);

    let filteredTasks = useFilteredTasks(project, {
        filterByAssignee,
        filterBy,
        user
    });
    filteredTasks = useSortedTasks(filteredTasks, sortBy);

    // Update task release status to match our calculated status
    filteredTasks = filteredTasks.map(task => {
        if (task.release?.id && releaseStatusMap.has(task.release.id)) {
            return {
                ...task,
                release: {
                    ...task.release,
                    status: releaseStatusMap.get(task.release.id)
                }
            };
        }
        return task;
    });

    // Helper function to sort tasks by status and position
    const sortTasksByStatusAndPosition = (tasks) => {
        // Get status order from the project
        const statusOrder = project?.statuses?.map(status => status.id);
        
        let sortedTasks = [...tasks];
        if(sortBy === 'manual') {
            sortedTasks.sort((a, b) => {
                const statusA = statusOrder.indexOf(a.status.id);
                const statusB = statusOrder.indexOf(b.status.id);
                if (statusA !== statusB) return statusA - statusB;
                return 0; // Add return for position comparison if needed
            });
        }
        if(groupByAssignee) {
            sortedTasks.sort((a, b) => {
                const assigneeA = a.assignee?.name;
                const assigneeB = b.assignee?.name;
                if(!assigneeA) return 1;
                if(!assigneeB) return -1;
                if (assigneeA !== assigneeB) return assigneeA.localeCompare(assigneeB);
                return 0;
            });
        }
        return sortedTasks;
    };

    // Process tasks and releases synchronously to avoid flash
    const { activeWeekTasks, activeReleases, backlogReleaseGroups } = useMemo(() => {
        console.log('Processing releases:', releases);
        console.log('All filtered tasks:', filteredTasks.length);

        console.log('Filtered tasks:', JSON.stringify(filteredTasks, null, 2));
        
        // 1. Active Week: All tasks with scope == 'tracked'
        const activeWeekTasks = filteredTasks.filter(task => task.scope === 'tracked' || !task.scope); // Backwards compatibility
        
        // 2. Backlog Section organization
        const backlogTasksAll = filteredTasks.filter(task => task.scope === 'backlog');
        
        // Separate releases into active and inactive
        const activeReleases = [];
        const inactiveReleases = [];
        
        releases.forEach(release => {
            // A release is active if it has a start date in the past or today
            const isActive = release.startDate && new Date(release.startDate) <= new Date();
            if (isActive) {
                activeReleases.push(release);
            } else {
                inactiveReleases.push(release);
            }
        });
        
        // Initialize release groups for INACTIVE releases only (for backlog section)
        // Active releases should not appear in backlog
        const backlogReleaseGroups = {};
        
        // Add only inactive releases to backlog groups
        inactiveReleases.forEach(release => {
            // Calculate active vs total tasks for this release
            const allReleaseTasks = filteredTasks.filter(task => task.release?.id === release._id);
            const activeReleaseTasks = allReleaseTasks.filter(task => task.scope === 'tracked' || !task.scope);
            const backlogReleaseTasks = allReleaseTasks.filter(task => task.scope === 'backlog');
            
            backlogReleaseGroups[release._id] = {
                release: {
                    id: release._id,
                    name: release.name,
                    status: release.status,
                    statusDetails: release.statusDetails,
                    targetDate: release.targetDate,
                    startDate: release.startDate
                },
                tasks: [],
                stats: {
                    activeCount: activeReleaseTasks.length,
                    totalCount: allReleaseTasks.length,
                    backlogCount: backlogReleaseTasks.length
                }
            };
        });
        
        // Add "No Release Assigned" group
        backlogReleaseGroups['no-release'] = {
            release: {
                id: 'no-release',
                name: 'No Release Assigned',
                status: 'unassigned',
                statusDetails: { name: 'Unassigned', color: 'gray' }
            },
            tasks: [],
            stats: {
                activeCount: 0,
                totalCount: 0,
                backlogCount: 0
            }
        };
        
        // Calculate stats for no-release group
        const noReleaseTasks = filteredTasks.filter(task => !task.release || !task.release.id);
        const noReleaseActiveTasks = noReleaseTasks.filter(task => task.scope === 'tracked' || !task.scope);
        const noReleaseBacklogTasks = noReleaseTasks.filter(task => task.scope === 'backlog');
        
        backlogReleaseGroups['no-release'].stats = {
            activeCount: noReleaseActiveTasks.length,
            totalCount: noReleaseTasks.length,
            backlogCount: noReleaseBacklogTasks.length
        };
        
        // Group backlog tasks
        backlogTasksAll.forEach(task => {
            if (task.release && task.release.id) {
                // Check if the release exists in our backlog groups (inactive releases only)
                if (backlogReleaseGroups[task.release.id]) {
                    backlogReleaseGroups[task.release.id].tasks.push(task);
                } else {
                    // If task has an active release or release doesn't exist, put in no-release group
                    backlogReleaseGroups['no-release'].tasks.push(task);
                }
            } else {
                backlogReleaseGroups['no-release'].tasks.push(task);
            }
        });
        
        // Sort tasks within each group
        Object.keys(backlogReleaseGroups).forEach(releaseId => {
            backlogReleaseGroups[releaseId].tasks = sortTasksByStatusAndPosition(backlogReleaseGroups[releaseId].tasks);
        });
        
        console.log('Active Week tasks:', activeWeekTasks.length);
        console.log('Active releases:', activeReleases.map(r => r.name));
        console.log('Backlog release groups:', Object.keys(backlogReleaseGroups).map(id => ({
            releaseId: id, 
            taskCount: backlogReleaseGroups[id].tasks.length
        })));
        
        return {
            activeWeekTasks: sortTasksByStatusAndPosition(activeWeekTasks),
            activeReleases,
            backlogReleaseGroups
        };
    }, [filteredTasks, releases, project?.statuses, sortBy, groupByAssignee]);

    const loadPreviousWeek = async () => {
        setLoadingPrevious(true);
        try {
            // Get the earliest loaded week's start date
            const firstWeek = previousWeeks.length > 0
                ? previousWeeks[0]
                : project.week;

            const startDate = new Date(firstWeek.startDate);

            // Create a new date for the previous week by subtracting 7 days from the start date
            const prevWeekDate = new Date(startDate);
            prevWeekDate.setDate(startDate.getDate() - 7);

            const prevWeek = getWeekNumber(prevWeekDate);
            const prevYear = prevWeekDate.getFullYear();

            const response = await axiosInstance.get('/tasks/query', {
                params: {
                    org: project.organizationSlug,
                    project: project.key,
                    week: prevWeek,
                    year: prevYear
                }
            });

            // Store the actual startDate in the week data
            setPreviousWeeks(prev => [{
                ...response.data.week,
                tasks: response.data.tasks,
                startDate: prevWeekDate.toISOString() // Ensure we store the actual date we calculated
            }, ...prev]);

            toast({
                description: "Previous week loaded successfully",
            });
        } catch (error) {
            console.error('Error loading previous week:', error);
            toast({
                variant: "destructive",
                description: "Failed to load previous week",
            });
        } finally {
            setLoadingPrevious(false);
        }
    };

    const loadNextWeek = async () => {
        setLoadingNext(true);
        try {
            // TODO: Implement loading next week
            await new Promise(resolve => setTimeout(resolve, 1000)); // Temporary simulation
        } catch (error) {
            console.error('Error loading next week:', error);
            toast({
                variant: "destructive",
                description: "Failed to load next week",
            });
        } finally {
            setLoadingNext(false);
        }
    };

    const handleTaskUpdate = (updatedTask) => {
        setProject((prevProject) => ({
            ...prevProject,
            tasks: prevProject.tasks.map((task) =>
                task.id === updatedTask.id
                    ? {
                        ...task,
                        ...updatedTask,
                        status: updatedTask.status,
                        assignee: updatedTask.assignee || null,
                        priority: updatedTask.priority,
                    }
                    : task
            ),
        }));
    };

    const onDragStart = () => {
        // Don't allow dragging when any tasks are selected for bulk actions
        if (selectedTasks.size > 0) {
            return false;
        }
        setIsDraggingAny(true);
    };

    const onDragEnd = async (result) => {
        setIsDraggingAny(false);
        
        // Don't process drag if any tasks are selected for bulk actions
        if (selectedTasks.size > 0 || !result.destination) return;

        const { source, destination, draggableId } = result;

        if (source.droppableId === destination.droppableId &&
            source.index === destination.index) {
            return;
        }

        // Get the source list based on droppableId
        let sourceList;
        let sourceReleaseId = null;
        
        if (source.droppableId === 'active') {
            sourceList = activeWeekTasks;
        } else if (source.droppableId === 'backlog') {
            sourceList = backlogReleaseGroups['no-release'].tasks;
            sourceReleaseId = 'no-release';
        } else if (source.droppableId.startsWith('release-')) {
            sourceReleaseId = source.droppableId.replace('release-', '');
            sourceList = backlogReleaseGroups[sourceReleaseId]?.tasks || [];
        }

        const taskToMove = sourceList?.find(task => task.id === draggableId);
        if (!taskToMove) return;

        // Only move the single dragged task
        const tasksToMove = [taskToMove];

        // Check if we're dragging from a backlog release to active week
        if (sourceReleaseId && sourceReleaseId !== 'no-release' && destination.droppableId === 'active') {
            // Store the drag operation details and show dialog
            setPendingDragOperation({
                source,
                destination,
                tasksToMove,
                sourceReleaseId,
                releaseName: backlogReleaseGroups[sourceReleaseId].release.name
            });
            setShowReleaseActivationDialog(true);
            return;
        }

        // Otherwise, proceed with immediate optimistic update and normal drag operation
        performOptimisticUpdate(source, destination, tasksToMove);
        await executeDragOperation(source, destination, tasksToMove);
    };

    const performOptimisticUpdate = (source, destination, tasksToMove, options = {}) => {
        // Create a copy of the current state
        const newActiveWeekTasks = [...activeWeekTasks];
        const newBacklogReleaseGroups = { ...backlogReleaseGroups };
        
        // Copy the tasks arrays for each release group
        Object.keys(newBacklogReleaseGroups).forEach(releaseId => {
            newBacklogReleaseGroups[releaseId] = {
                ...newBacklogReleaseGroups[releaseId],
                tasks: [...newBacklogReleaseGroups[releaseId].tasks]
            };
        });

        // Remove tasks from source
        if (source.droppableId === 'active') {
            tasksToMove.forEach(task => {
                const index = newActiveWeekTasks.findIndex(t => t.id === task.id);
                if (index > -1) newActiveWeekTasks.splice(index, 1);
            });
        } else if (source.droppableId === 'backlog') {
            tasksToMove.forEach(task => {
                const index = newBacklogReleaseGroups['no-release'].tasks.findIndex(t => t.id === task.id);
                if (index > -1) newBacklogReleaseGroups['no-release'].tasks.splice(index, 1);
            });
        } else if (source.droppableId.startsWith('release-')) {
            const releaseId = source.droppableId.replace('release-', '');
            tasksToMove.forEach(task => {
                const index = newBacklogReleaseGroups[releaseId].tasks.findIndex(t => t.id === task.id);
                if (index > -1) newBacklogReleaseGroups[releaseId].tasks.splice(index, 1);
            });
        }

        // Prepare updated tasks based on destination
        const updatedTasks = tasksToMove.map(task => {
            let newStatus = task.status;
            let newRelease = task.release;
            let newScope = task.scope;

            if (destination.droppableId === 'active') {
                // Moving to active week - set scope to tracked
                newScope = 'tracked';
                // Don't automatically change status anymore - just change scope
                // Handle release assignment based on options
                if (options && options.removeFromRelease) {
                    newRelease = null;
                } else if (options && options.keepRelease) {
                    // Explicitly keep the current release
                    newRelease = task.release;
                } else {
                    // Default behavior - keep existing release
                    newRelease = task.release;
                }
            } else if (destination.droppableId === 'backlog') {
                // Moving to no-release backlog - set scope to backlog
                newScope = 'backlog';
                // Status can remain as is when moving to backlog
                // When moving to backlog, server will auto-remove from active releases
                // but we keep inactive releases in the optimistic update
                if (task.release?.id) {
                    const taskRelease = releases.find(r => r._id === task.release.id);
                    const isActiveRelease = taskRelease?.startDate && new Date(taskRelease.startDate) <= new Date();
                    if (isActiveRelease) {
                        newRelease = null;
                    }
                } else {
                    newRelease = null;
                }
            } else if (destination.droppableId.startsWith('release-')) {
                // Moving to a release - set scope to backlog
                const releaseId = destination.droppableId.replace('release-', '');
                newScope = 'backlog';
                // Status can remain as is when moving to backlog
                newRelease = newBacklogReleaseGroups[releaseId]?.release || null;
            }

            return {
                ...task,
                status: newStatus,
                release: newRelease,
                scope: newScope
            };
        });

        // Add tasks to destination
        if (destination.droppableId === 'active') {
            newActiveWeekTasks.splice(destination.index, 0, ...updatedTasks);
        } else if (destination.droppableId === 'backlog') {
            newBacklogReleaseGroups['no-release'].tasks.splice(destination.index, 0, ...updatedTasks);
        } else if (destination.droppableId.startsWith('release-')) {
            const releaseId = destination.droppableId.replace('release-', '');
            if (newBacklogReleaseGroups[releaseId]) {
                newBacklogReleaseGroups[releaseId].tasks.splice(destination.index, 0, ...updatedTasks);
            }
        }

        // Update the main project state for optimistic update
        // The useMemo hooks will automatically recompute activeWeekTasks and backlogReleaseGroups
        setProject(prevProject => ({
            ...prevProject,
            tasks: prevProject.tasks.map(task => {
                const movedTask = tasksToMove.find(t => t.id === task.id);
                if (movedTask) {
                    const updatedTask = updatedTasks.find(t => t.id === task.id);
                    return {
                        ...task,
                        status: updatedTask.status,
                        release: updatedTask.release,
                        scope: updatedTask.scope,
                        position: destination.index + tasksToMove.findIndex(t => t.id === task.id)
                    };
                }
                return task;
            })
        }));
        
        // Clear selection after drag
        setSelectedTasks(new Set());
    };

    const handleTaskSelection = (taskId, isChecked) => {
        setSelectedTasks(prev => {
            const newSelection = new Set(prev);
            if (isChecked) {
                newSelection.add(taskId);
            } else {
                newSelection.delete(taskId);
            }
            return newSelection;
        });
    };

    const handleTaskCreated = (newTask) => {
        setProject(prevProject => ({
            ...prevProject,
            tasks: [...prevProject.tasks, newTask]
        }));

        // The useEffect will automatically re-group tasks based on the new logic
        // No need for manual updates here since it depends on status and release assignment
    };

    const handleCreateRelease = async () => {
        if (!newReleaseName.trim()) {
            toast({
                description: "Please provide a release name",
                variant: "destructive"
            });
            return;
        }

        try {
            const response = await axiosInstance.post(`/projects/${project.organizationSlug}/${project.key}/releases`, {
                name: newReleaseName,
                targetDate: newReleaseTargetDate || null,
                startDate: null
            });

            // Update project releases in local state
            setProject(prevProject => ({
                ...prevProject,
                releases: [...(prevProject?.releases || []), response.data.release]
            }));
            
            setNewReleaseName("");
            setNewReleaseTargetDate(null);
            setDatePickerOpen(false);
            setShowNewReleaseForm(false);
            
            toast({
                description: "Release created successfully",
            });
        } catch (error) {
            console.error('Error creating release:', error);
            toast({
                variant: "destructive",
                description: error.response?.data?.message || "Failed to create release",
            });
        }
    };

    const handleActivateRelease = (releaseId) => {
        const release = releases.find(r => r._id === releaseId);
        if (!release) return;
        
        setReleaseToActivate(release);
        setShowReleaseMenuActivationDialog(true);
    };

    const handleShowReleaseDetails = (releaseId) => {
        onShowReleaseDetails && onShowReleaseDetails(releaseId);
    };

    const handleReleaseUpdate = (updatedRelease) => {
        // Update the release in the project state
        setProject(prevProject => ({
            ...prevProject,
            releases: prevProject?.releases?.map(r => 
                r._id === updatedRelease._id 
                    ? updatedRelease
                    : r
            ) || []
        }));

        setSelectedRelease(updatedRelease);
    };

    const handleShipRelease = (release) => {
        setReleaseToShip(release);
        setShowReleaseShipDialog(true);
    };

    const handleReleaseActivate = (release) => {
        // Show the same confirmation dialog as dropdown menu
        setReleaseToActivate(release);
        setShowReleaseMenuActivationDialog(true);
    };

    const handleShowReleaseDeactivation = (release) => {
        setReleaseToDeactivate(release);
        setShowReleaseDeactivationDialog(true);
    };

    const handleDeleteRelease = (releaseToDelete) => {
        setReleaseToDelete(releaseToDelete);
        setShowReleaseDeleteDialog(true);
    };

    const confirmDeleteRelease = async () => {
        if (!releaseToDelete) return;
        
        setIsDeletingRelease(true);
        try {
            await axiosInstance.delete(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToDelete._id}`);
            
            // Refetch project data to ensure consistency
            if (refetchProject) {
                await refetchProject();
            }
            
            toast({
                description: `Release "${releaseToDelete.name}" deleted successfully`,
            });
        } catch (error) {
            console.error('Error deleting release:', error);
            toast({
                variant: "destructive",
                description: error.response?.data?.message || "Failed to delete release",
            });
        } finally {
            setIsDeletingRelease(false);
            setShowReleaseDeleteDialog(false);
            setReleaseToDelete(null);
        }
    };



    const confirmActivateRelease = async () => {
        if (!releaseToActivate) return;
        
        setIsActivatingRelease(true);
        try {
            const releaseId = releaseToActivate._id;
            const releaseTasks = backlogReleaseGroups[releaseId]?.tasks || [];
            
            // Single API call - backend handles bulk task updates automatically with transaction
            await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseId}`, {
                startDate: new Date()
            });
            
            // Refetch project data to ensure consistency instead of optimistic updates
            if (refetchProject) {
                await refetchProject();
            }
            
            toast({
                description: `Release "${releaseToActivate.name}" activated with ${releaseTasks.length} tasks`,
            });
        } catch (error) {
            console.error('Error activating release:', error);
            toast({
                variant: "destructive",
                description: error.response?.data?.message || "Failed to activate release",
            });
        } finally {
            setIsActivatingRelease(false);
            setShowReleaseMenuActivationDialog(false);
            setReleaseToActivate(null);
        }
    };

    const confirmDeactivateRelease = async () => {
        if (!releaseToDeactivate) return;
        
        setIsDeactivatingRelease(true);
        try {
            const activeReleaseTasks = activeWeekTasks.filter(task => task.release?.id === releaseToDeactivate._id);
            
            // Single API call - backend handles bulk task updates automatically with transaction
            await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToDeactivate._id}`, {
                startDate: null
            });
            
            // Refetch project data to ensure consistency instead of optimistic updates
            if (refetchProject) {
                await refetchProject();
            }
            
            toast({
                description: `Release "${releaseToDeactivate.name}" moved to backlog with ${activeReleaseTasks.length} tasks`,
            });
        } catch (error) {
            console.error('Error deactivating release:', error);
            toast({
                variant: "destructive",
                description: error.response?.data?.message || "Failed to move release to backlog",
            });
        } finally {
            setIsDeactivatingRelease(false);
            setShowReleaseDeactivationDialog(false);
            setReleaseToDeactivate(null);
        }
    };

    const confirmShipRelease = async () => {
        if (!releaseToShip) return;
        
        setIsShippingRelease(true);
        try {
            const newActualReleaseDate = new Date();
            
            // Single API call - backend handles release shipping
            await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToShip._id}`, {
                actualReleaseDate: newActualReleaseDate
            });
            
            // Refetch project data to ensure consistency
            if (refetchProject) {
                await refetchProject();
            }
            
            toast({
                description: `Release "${releaseToShip.name}" marked as shipped`,
            });
        } catch (error) {
            console.error('Error shipping release:', error);
            toast({
                variant: "destructive",
                description: error.response?.data?.message || "Failed to ship release",
            });
        } finally {
            setIsShippingRelease(false);
            setShowReleaseShipDialog(false);
            setReleaseToShip(null);
        }
    };

    const executeDragOperation = async (source, destination, tasksToMove, options = {}) => {
        try {
            const taskToMove = tasksToMove[0]; // Primary task for determining changes
            let newStatus = taskToMove.status.id;
            let newReleaseId = taskToMove.release?.id || null;
            let statusChanged = false;
            let releaseChanged = false;

            // Handle special options for release activation
            if (options.activateRelease) {
                // Activate the entire release
                const releaseId = options.releaseId;
                const release = releases.find(r => r._id === releaseId);
                
                if (release) {
                    // Update the release start date
                    await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseId}`, {
                        startDate: new Date()
                    });
                    
                    // Move all tasks in the release to active week (change scope only)
                    const allReleaseTasks = backlogReleaseGroups[releaseId]?.tasks || [];
                    await Promise.all(allReleaseTasks.map(task => 
                        axiosInstance.patch(`/tasks/${project.organizationSlug}/${task.key}`, {
                            scope: 'tracked'
                        })
                    ));
                    
                    // Refresh the data
                    if (refetchProject) {
                        await refetchProject();
                    }
                    
                    // Update project state to reflect the activation
                    setProject(prevProject => ({
                        ...prevProject,
                        tasks: prevProject.tasks.map(task => {
                            const releaseTask = allReleaseTasks.find(t => t.id === task.id);
                            if (releaseTask) {
                                return {
                                    ...task,
                                    scope: 'tracked'
                                };
                            }
                            return task;
                        })
                    }));
                    
                    toast({
                        description: `Release "${release.name}" activated with ${allReleaseTasks.length} tasks`,
                    });
                    return;
                }
            }

            // Determine changes based on destination
            let newScope = taskToMove.scope;
            let scopeChanged = false;
            
            if (destination.droppableId === 'backlog') {
                // Moving to backlog: set scope to backlog
                newScope = 'backlog';
                scopeChanged = taskToMove.scope !== 'backlog';
                
                // When moving to backlog, the server will automatically remove from active releases
                // So we don't need to handle release changes here
                newReleaseId = null;
                releaseChanged = false;
            } else if (destination.droppableId === 'active') {
                // Moving to active week: set scope to tracked
                newScope = 'tracked';
                scopeChanged = taskToMove.scope !== 'tracked';
                
                // Don't automatically change status anymore - just change scope
                // Handle release based on options
                if (options.removeFromRelease) {
                    newReleaseId = null;
                    releaseChanged = taskToMove.release?.id != null;
                } else if (options.keepRelease) {
                    // Keep the current release - no change
                    newReleaseId = taskToMove.release?.id;
                    releaseChanged = false;
                }
            } else if (destination.droppableId.startsWith('release-')) {
                // Moving to release section: set scope to backlog
                newScope = 'backlog';
                scopeChanged = taskToMove.scope !== 'backlog';
                newReleaseId = destination.droppableId.replace('release-', '');
                releaseChanged = taskToMove.release?.id !== newReleaseId;
            }

            // Make API calls for changes - do them sequentially to avoid conflicts
            for (const task of tasksToMove) {
                try {
                    const updateData = {
                        position: destination.index + tasksToMove.indexOf(task)
                    };
                    
                    // Add scope change if needed
                    if (scopeChanged) {
                        updateData.scope = newScope;
                    }
                    
                    // Add status change if needed
                    if (statusChanged) {
                        updateData.status = newStatus;
                    }
                    
                    // Handle release changes
                    if (releaseChanged && destination.droppableId !== 'backlog') {
                        if (newReleaseId && newReleaseId !== 'no-release') {
                            await axiosInstance.post(`/tasks/${project.organizationSlug}/${task.key}/release`, {
                                release: { _id: newReleaseId },
                                isNewRelease: false
                            });
                        } else if (options.removeFromRelease) {
                            await axiosInstance.delete(`/tasks/${project.organizationSlug}/${task.key}/release`);
                        }
                    }
                    
                    // Update task with scope, status, and position changes
                    if (scopeChanged || statusChanged || source.droppableId !== destination.droppableId || source.index !== destination.index) {
                        await axiosInstance.patch(`/tasks/${project.organizationSlug}/${task.key}`, updateData);
                    }
                } catch (error) {
                    // If we get a version error, log it but continue with other tasks
                    if (error.response?.data?.message?.includes('VersionError')) {
                        console.warn(`Version conflict for task ${task.key}, skipping...`);
                    } else {
                        throw error;
                    }
                }
            }

            toast({
                description: `${tasksToMove.length} task${tasksToMove.length > 1 ? 's' : ''} moved successfully`,
            });
        } catch (error) {
            console.error('Error moving tasks:', error);
            toast({
                variant: "destructive",
                description: "Failed to move tasks. Refreshing...",
            });
            
            // On error, refresh the entire project to restore correct state
            window.location.reload();
        }
    };

    const handleBulkMoveToTarget = async (target) => {
        if (!target || selectedTasks.size === 0) return;

        // Get all selected tasks
        const tasksToMove = [];
        const allTasks = [
            ...activeWeekTasks,
            ...Object.values(backlogReleaseGroups).flatMap(group => group.tasks)
        ];
        
        allTasks.forEach(task => {
            if (selectedTasks.has(task.id)) {
                tasksToMove.push(task);
            }
        });

        if (tasksToMove.length === 0) return;

        // Check if we're moving tasks from a release to active week
        if (target === 'active' || target.startsWith('active-release-')) {
            // Check if any selected tasks are from an inactive release
            const tasksFromReleases = tasksToMove.filter(task => 
                task.release?.id && task.release.id !== 'no-release'
            );
            
            // Skip activation dialog for active-release targets since they're already active
            if (target.startsWith('active-release-')) {
                // Proceed directly with bulk move to specific active release
                await performBulkMove(tasksToMove, target);
                return;
            }
            
            if (tasksFromReleases.length > 0) {
                // Get unique releases
                const uniqueReleases = [...new Set(tasksFromReleases.map(t => t.release.id))];
                
                if (uniqueReleases.length === 1) {
                    // All tasks from same release - show release activation dialog
                    const releaseId = uniqueReleases[0];
                    const releaseName = backlogReleaseGroups[releaseId]?.release.name || 'Unknown Release';
                    
                    setPendingDragOperation({
                        tasksToMove,
                        isBulkOperation: true,
                        sourceReleaseId: releaseId,
                        releaseName
                    });
                    setShowReleaseActivationDialog(true);
                    return;
                } else {
                    // Tasks from multiple releases - just move them without activation option
                    toast({
                        description: "Moving tasks from multiple releases to active week",
                    });
                }
            }
        }

        // Proceed with bulk move
        await performBulkMove(tasksToMove, target);
    };

    const handleBulkMove = async () => {
        if (!bulkMoveTarget || selectedTasks.size === 0) return;

        // Get all selected tasks
        const tasksToMove = [];
        const allTasks = [
            ...activeWeekTasks,
            ...Object.values(backlogReleaseGroups).flatMap(group => group.tasks)
        ];
        
        allTasks.forEach(task => {
            if (selectedTasks.has(task.id)) {
                tasksToMove.push(task);
            }
        });

        if (tasksToMove.length === 0) return;

        // Check if we're moving tasks from a release to active week
        if (bulkMoveTarget === 'active' || bulkMoveTarget.startsWith('active-release-')) {
            // Check if any selected tasks are from an inactive release
            const tasksFromReleases = tasksToMove.filter(task => 
                task.release?.id && task.release.id !== 'no-release'
            );
            
            // Skip activation dialog for active-release targets since they're already active
            if (bulkMoveTarget.startsWith('active-release-')) {
                // Proceed directly with bulk move to specific active release
                await performBulkMove(tasksToMove);
                return;
            }
            
            if (tasksFromReleases.length > 0) {
                // Get unique releases
                const uniqueReleases = [...new Set(tasksFromReleases.map(t => t.release.id))];
                
                if (uniqueReleases.length === 1) {
                    // All tasks from same release - show release activation dialog
                    const releaseId = uniqueReleases[0];
                    const releaseName = backlogReleaseGroups[releaseId]?.release.name || 'Unknown Release';
                    
                    setPendingDragOperation({
                        tasksToMove,
                        isBulkOperation: true,
                        sourceReleaseId: releaseId,
                        releaseName
                    });
                    setShowReleaseActivationDialog(true);
                    return;
                } else {
                    // Tasks from multiple releases - just move them without activation option
                    toast({
                        description: "Moving tasks from multiple releases to active week",
                    });
                }
            }
        }

        // Proceed with bulk move
        await performBulkMove(tasksToMove);
    };

    const performBulkMove = async (tasksToMove, target, options = {}) => {
        // Perform optimistic update
        const newActiveWeekTasks = [...activeWeekTasks];
        const newBacklogReleaseGroups = { ...backlogReleaseGroups };
        
        // Deep copy release groups
        Object.keys(newBacklogReleaseGroups).forEach(releaseId => {
            newBacklogReleaseGroups[releaseId] = {
                ...newBacklogReleaseGroups[releaseId],
                tasks: [...newBacklogReleaseGroups[releaseId].tasks]
            };
        });

        // Remove tasks from their current locations
        tasksToMove.forEach(task => {
            // Remove from active week
            const activeIndex = newActiveWeekTasks.findIndex(t => t.id === task.id);
            if (activeIndex > -1) {
                newActiveWeekTasks.splice(activeIndex, 1);
            }
            
            // Remove from all release groups
            Object.keys(newBacklogReleaseGroups).forEach(releaseId => {
                const index = newBacklogReleaseGroups[releaseId].tasks.findIndex(t => t.id === task.id);
                if (index > -1) {
                    newBacklogReleaseGroups[releaseId].tasks.splice(index, 1);
                }
            });
        });

        // Update tasks and add to destination
        const updatedTasks = tasksToMove.map(task => {
            let newStatus = task.status;
            let newRelease = task.release;
            let newScope = task.scope;

            if (target === 'active') {
                // Moving to active week - set scope to tracked
                newScope = 'tracked';
                // Don't automatically change status anymore - just change scope
                // Handle release assignment based on options
                if (options.removeFromRelease) {
                    newRelease = null;
                } else if (options.keepRelease) {
                    newRelease = task.release; // Keep current release
                } else {
                    newRelease = task.release; // Default behavior - keep current release
                }
            } else if (target.startsWith('active-release-')) {
                // Moving to a specific active release - set scope to tracked
                newScope = 'tracked';
                const releaseId = target.replace('active-release-', '');
                const targetRelease = activeReleases.find(r => r._id === releaseId);
                if (targetRelease) {
                    // Don't automatically change status anymore - just change scope
                    newRelease = {
                        id: targetRelease._id,
                        name: targetRelease.name,
                        status: targetRelease.status,
                        statusDetails: targetRelease.statusDetails,
                        startDate: targetRelease.startDate,
                        targetDate: targetRelease.targetDate
                    };
                }
            } else if (target === 'backlog') {
                // Moving to no-release backlog - set scope to backlog
                newScope = 'backlog';
                // Status can remain as is when moving to backlog
                newRelease = null;
            } else {
                // Moving to a specific release - set scope to backlog
                newScope = 'backlog';
                // Status can remain as is when moving to backlog
                newRelease = backlogReleaseGroups[target]?.release || null;
            }

            return {
                ...task,
                status: newStatus,
                release: newRelease,
                scope: newScope
            };
        });

        // Add tasks to destination
        if (target === 'active' || target.startsWith('active-release-')) {
            newActiveWeekTasks.push(...updatedTasks);
        } else if (target === 'backlog') {
            newBacklogReleaseGroups['no-release'].tasks.push(...updatedTasks);
        } else {
            if (newBacklogReleaseGroups[target]) {
                newBacklogReleaseGroups[target].tasks.push(...updatedTasks);
            }
        }

        // Update state optimistically
        // The useMemo hooks will automatically recompute activeWeekTasks and backlogReleaseGroups
        setSelectedTasks(new Set());

        // Update project state for optimistic update
        setProject(prevProject => ({
            ...prevProject,
            tasks: prevProject.tasks.map(task => {
                const movedTask = tasksToMove.find(t => t.id === task.id);
                if (movedTask) {
                    const updatedTask = updatedTasks.find(t => t.id === task.id);
                    return {
                        ...task,
                        status: updatedTask.status,
                        release: updatedTask.release,
                        scope: updatedTask.scope
                    };
                }
                return task;
            })
        }));

        // Make bulk API call
        try {
            const bulkUpdates = tasksToMove.map(task => {
                const updatedTask = updatedTasks.find(t => t.id === task.id);
                
                return {
                    taskKey: task.key,
                    updates: {
                        scope: updatedTask.scope,
                        status: updatedTask.status.id,
                        releaseId: updatedTask.release?.id || null,
                        removeFromRelease: target === 'backlog' && task.release?.id
                    }
                };
            });

            await axiosInstance.post(`/tasks/${project.organizationSlug}/bulk-update`, {
                tasks: bulkUpdates
            });

            // Update project state
            setProject(prevProject => ({
                ...prevProject,
                tasks: prevProject.tasks.map(task => {
                    const movedTask = tasksToMove.find(t => t.id === task.id);
                    if (movedTask) {
                        const updatedTask = updatedTasks.find(t => t.id === task.id);
                        return {
                            ...task,
                            status: updatedTask.status,
                            release: updatedTask.release,
                            scope: updatedTask.scope
                        };
                    }
                    return task;
                })
            }));

            toast({
                description: `${tasksToMove.length} task${tasksToMove.length > 1 ? 's' : ''} moved successfully`,
            });
        } catch (error) {
            console.error('Error moving tasks:', error);
            toast({
                variant: "destructive",
                description: "Failed to move tasks. Refreshing...",
            });
            window.location.reload();
        }
    };

    return (
        <>
            <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <div className="space-y-6 pb-6">
                    {/* Load Previous Week Button */}
                    <div className="flex justify-center">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadPreviousWeek}
                            disabled={loadingPrevious}
                        >
                            {loadingPrevious ? (
                                "Loading..."
                            ) : (
                                <>
                                    <ChevronUp className="w-4 h-4 mr-2" />
                                    Browse Previous Week
                                </>
                            )}
                        </Button>
                    </div>

                    {/* Previous Weeks Sections */}
                    {previousWeeks.map((week, index) => (
                        <Card key={`${week.year}-${week.number}`}>
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <div className="text-lg">
                                        Week {week.number} {' • '} Previous Week
                                    </div>
                                    <div className="text-sm text-muted-foreground font-normal">
                                        {week.startDateFormatted} - {week.endDateFormatted}
                                    </div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {week.releases?.length > 0 && (
                                    <div className="flex items-center flex-wrap gap-2 mb-3">
                                        {week.releases.map((release) => {
                                            const colors = getReleaseStatusColor(release.status);
                                            const taskCount = week.tasks.filter(t => t.release?.id === release._id).length;
                                            return (
                                                <button
                                                    key={release._id}
                                                    className={cn(
                                                        "flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-200 cursor-pointer",
                                                        colors.bg,
                                                        colors.text,
                                                        colors.border
                                                    )}
                                                    onClick={() => {
                                                        handleShowReleaseDetails(release);
                                                    }}
                                                >
                                                    <FiPackage className="h-3 w-3" />
                                                    <span className="text-xs font-medium">{release.name}</span>
                                                    {taskCount > 0 && (
                                                        <span className="text-xs opacity-70">({taskCount})</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                <ScrollArea className="w-full">
                                    <div className="space-y-2">
                                        {week.tasks.length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-4">
                                                No completed tasks this week
                                            </p>
                                        ) : (
                                            week.tasks.map((task, taskIndex) => (
                                                <TaskListItem
                                                    key={task.id}
                                                    task={task}
                                                    organizationSlug={project.organizationSlug}
                                                    onTaskUpdate={handleTaskUpdate}
                                                    onSelectionChange={handleTaskSelection}
                                                    isSelected={selectedTasks.has(task.id)}
                                                    isDragDisabled={selectedTasks.size > 0}
                                                />
                                            ))
                                        )}
                                    </div>
                                    <ScrollBar orientation="horizontal" />
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    ))}

                    {/* Current Week Section */}
                    <Card>
                        <CardHeader className="pb-4">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-4 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Zap className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                            <span className="text-lg font-semibold whitespace-nowrap">Week {project.week.weekNumber}</span>
                                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                                                Active
                                            </Badge>
                                        </div>

                                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                                            {activeWeekTasks.length} {activeWeekTasks.length === 1 ? 'task' : 'tasks'}
                                        </div>

                                        {/* Active Releases - inline */}
                                        {activeReleases.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                {(() => {
                                                    // Sort releases by task count (descending)
                                                    const sortedReleases = [...activeReleases].sort((a, b) => {
                                                        const aTaskCount = activeWeekTasks.filter(task => task.release?.id === a._id).length;
                                                        const bTaskCount = activeWeekTasks.filter(task => task.release?.id === b._id).length;
                                                        return bTaskCount - aTaskCount;
                                                    });
                                                    
                                                    const visibleReleases = sortedReleases.slice(0, 2);
                                                    const hiddenReleases = sortedReleases.slice(2);
                                                    
                                                    return (
                                                        <>
                                                            {visibleReleases.map((release) => {
                                                                const colors = getReleaseStatusColor(release.status);
                                                                const taskCount = activeWeekTasks.filter(task => task.release?.id === release._id).length;
                                                                return (
                                                                    <button
                                                                        key={release._id}
                                                                        className={cn(
                                                                            "flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all duration-200 cursor-pointer",
                                                                            colors.bg,
                                                                            colors.text,
                                                                            colors.border,
                                                                            colors.hover
                                                                        )}
                                                                        onClick={() => {
                                                                            handleShowReleaseDetails(release._id);
                                                                        }}
                                                                    >
                                                                        <FiPackage className="h-3 w-3" />
                                                                        <span className="text-xs font-medium">{release.name}</span>
                                                                        {taskCount > 0 && (
                                                                            <span className="text-xs opacity-70">({taskCount})</span>
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                            
                                                            {hiddenReleases.length > 0 && (
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs text-muted-foreground">
                                                                            +{hiddenReleases.length} more
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="start" className="w-64">
                                                                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                                                                            Additional Active Releases
                                                                        </DropdownMenuLabel>
                                                                        <DropdownMenuSeparator />
                                                                        {hiddenReleases.map((release) => {
                                                                            const colors = getReleaseStatusColor(release.status);
                                                                            const taskCount = activeWeekTasks.filter(task => task.release?.id === release._id).length;
                                                                            return (
                                                                                <DropdownMenuItem
                                                                                    key={release._id}
                                                                                    onClick={() => {
                                                                                        handleShowReleaseDetails(release._id);
                                                                                    }}
                                                                                    className="cursor-pointer"
                                                                                >
                                                                                    <FiPackage className={cn("h-3 w-3 mr-2", colors.icon)} />
                                                                                    <span className="flex-1 text-sm">{release.name}</span>
                                                                                    {taskCount > 0 && (
                                                                                        <span className="text-xs text-muted-foreground">
                                                                                            {taskCount} {taskCount === 1 ? 'task' : 'tasks'}
                                                                                        </span>
                                                                                    )}
                                                                                </DropdownMenuItem>
                                                                            );
                                                                        })}
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <div className="text-sm text-muted-foreground font-medium whitespace-nowrap">
                                            {project.week.startDate} - {project.week.endDate}
                                        </div>
                                        
                                        {/* Warning icon for active week issues */}
                                        {(() => {
                                            const issues = [];
                                            
                                            // Check 1: Active week has tasks from inactive releases
                                            const inactiveReleaseTasks = activeWeekTasks.filter(task => {
                                                if (!task.release?.id) return false;
                                                const taskRelease = releases.find(r => r._id === task.release.id);
                                                return taskRelease && (!taskRelease.startDate || new Date(taskRelease.startDate) > new Date());
                                            });
                                            
                                            if (inactiveReleaseTasks.length > 0) {
                                                issues.push({
                                                    type: 'inactive-release-tasks',
                                                    count: inactiveReleaseTasks.length,
                                                    message: `${inactiveReleaseTasks.length} ${inactiveReleaseTasks.length === 1 ? 'task is' : 'tasks are'} from upcoming releases that haven't been activated yet.`
                                                });
                                            }
                                            
                                            // Check 2: Active releases with no tasks assigned
                                            const activeReleasesWithNoTasks = activeReleases.filter(release => {
                                                const releaseTaskCount = activeWeekTasks.filter(task => task.release?.id === release._id).length;
                                                return releaseTaskCount === 0;
                                            });
                                            
                                            if (activeReleasesWithNoTasks.length > 0) {
                                                const releaseNames = activeReleasesWithNoTasks.map(r => r.name).join(', ');
                                                issues.push({
                                                    type: 'empty-active-releases',
                                                    count: activeReleasesWithNoTasks.length,
                                                    message: `${activeReleasesWithNoTasks.length === 1 ? 'Active release' : 'Active releases'} "${releaseNames}" ${activeReleasesWithNoTasks.length === 1 ? 'has' : 'have'} no tasks assigned.`
                                                });
                                            }
                                            
                                            if (issues.length === 0) return null;
                                            
                                            return (
                                                <TooltipProvider delayDuration={0}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div className="h-7 w-7 flex items-center justify-center text-amber-600 dark:text-amber-400 cursor-default">
                                                                <AlertTriangle className="h-4 w-4" />
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="left" className="max-w-xs">
                                                            <div className="space-y-2">
                                                                <p className="font-medium text-sm">Active Week Issues</p>
                                                                {issues.map((issue, index) => (
                                                                    <p key={index} className="text-xs text-muted-foreground">
                                                                        {issue.message}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                    <Droppable droppableId="active">
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={cn(
                                                    "space-y-2 min-h-[60px] rounded-xl border-2 border-dashed transition-colors",
                                                    snapshot.isDraggingOver 
                                                        ? "border-violet-300 dark:border-violet-600" 
                                                        : "border-neutral-200 dark:border-neutral-800",
                                                    activeWeekTasks.length === 0 && "px-4 py-6"
                                                )}
                                            >
                                                {activeWeekTasks.length === 0 ? (
                                                    <div className="text-center">
                                                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                                            Drop tasks here or create new ones
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className={cn("space-y-2", activeWeekTasks.length > 0 && "p-2")}>
                                                        {activeWeekTasks.map((task, index) => (
                                                            <Draggable
                                                                key={task.id}
                                                                draggableId={task.id}
                                                                index={index}
                                                                isDragDisabled={selectedTasks.size > 0}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                    >
                                                                        <TaskListItem
                                                                            task={task}
                                                                            organizationSlug={project.organizationSlug}
                                                                            onTaskUpdate={handleTaskUpdate}
                                                                            onSelectionChange={handleTaskSelection}
                                                                            isSelected={selectedTasks.has(task.id)}
                                                                            isDragging={snapshot.isDragging}
                                                                            isDragDisabled={selectedTasks.size > 0}
                                                                            data-task-id={task.id}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        <QuickAddTask
                                                            status="todo"
                                                            projectId={project.id}
                                                            organizationSlug={project.organizationSlug}
                                                            assigneeId="unassigned"
                                                            onTaskCreated={handleTaskCreated}
                                                            alwaysShow={true}
                                                            buttonText="Create task in active week"
                                                            textSize="text-xs"
                                                        />
                                                    </div>
                                                )}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                        </CardContent>
                    </Card>

                    {/* Backlog Section with Release Groups */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2 text-lg">
                                    <Archive className="h-4 w-4 text-neutral-600" />
                                    Backlog
                                </CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowNewReleaseForm(true)}
                                    className="h-8 px-3 text-xs"
                                >
                                    <Plus className="w-3 h-3 mr-1.5" />
                                    Add Release
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-6">
                                {Object.entries(backlogReleaseGroups)
                                    .filter(([releaseId]) => releaseId !== 'no-release')
                                    .map(([releaseId, releaseGroup]) => (
                                    <div key={releaseId} className="space-y-3">
                                        {/* Release Header */}
                                        <div className="flex items-center justify-between py-2">
                                            <div className="flex items-center gap-3">
                                                {releaseId !== 'no-release' && (
                                                    <>
                                                                                                                    <div className="flex items-center gap-2">
                                                                <FiPackage className="h-4 w-4 text-neutral-500" />
                                                                <span className="font-semibold text-neutral-900 dark:text-neutral-100">{releaseGroup.release.name}</span>
                                                            </div>
                                                            <Badge variant="secondary" className="text-xs bg-neutral-100 text-neutral-600 dark:bg-neutral-900/20 dark:text-neutral-400 border-neutral-200 dark:border-neutral-700">
                                                                Upcoming
                                                            </Badge>
                                                    </>
                                                )}
                                                {releaseId === 'no-release' && (
                                                    <span className="font-semibold text-neutral-900 dark:text-neutral-100">{releaseGroup.release.name}</span>
                                                )}
                                                
                                                {/* Task counts and metadata */}
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <span>
                                                        {releaseGroup.tasks.length} backlog
                                                    </span>
                                                    
                                                    {releaseGroup.stats && releaseGroup.stats.activeCount > 0 && (
                                                        <>
                                                            <span className="text-neutral-300 dark:text-neutral-700">•</span>
                                                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                                                {releaseGroup.stats.activeCount} active
                                                            </span>
                                                        </>
                                                    )}

                                                    {releaseId !== 'no-release' && releaseGroup.release.targetDate && (
                                                        <>
                                                            <span className="text-neutral-300 dark:text-neutral-700">•</span>
                                                            <span className="text-amber-600 dark:text-amber-400">
                                                                Target {format(new Date(releaseGroup.release.targetDate), 'MMM d')}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1">
                                                {/* Warning icon for inactive releases with active tasks */}
                                                {releaseId !== 'no-release' && releaseGroup.stats && releaseGroup.stats.activeCount > 0 && (
                                                    <TooltipProvider delayDuration={0}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className="h-7 w-7 flex items-center justify-center text-amber-600 dark:text-amber-400 cursor-default">
                                                                    <AlertTriangle className="h-4 w-4" />
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="left" className="max-w-sm">
                                                                <div className="space-y-1">
                                                                    <p className="font-medium text-sm">Mixed Release Status</p>
                                                                    <p className="text-xs">
                                                                        This inactive release has <strong>{releaseGroup.stats.activeCount} {releaseGroup.stats.activeCount === 1 ? 'task' : 'tasks'}</strong> in the active week.
                                                                    </p>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Consider activating the release or moving tasks back to backlog for better organization.
                                                                    </p>
                                                                </div>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                
                                                {releaseId !== 'no-release' && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-foreground">
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-48">
                                                            <DropdownMenuItem 
                                                                onClick={() => handleActivateRelease(releaseGroup.release.id)}
                                                            >
                                                                Activate Release
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={() => handleShowReleaseDetails(releaseGroup.release.id)}
                                                            >
                                                                View Release Details
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem 
                                                                className="text-red-600 dark:text-red-400"
                                                                onClick={() => handleDeleteRelease({
                                                                    _id: releaseGroup.release.id,
                                                                    name: releaseGroup.release.name
                                                                })}
                                                            >
                                                                Delete Release
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </div>
                                        </div>
                                        


                                        {/* Tasks Drop Zone */}
                                        <Droppable droppableId={`release-${releaseId}`}>
                                            {(provided, snapshot) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    className={cn(
                                                        "space-y-2 min-h-[60px] rounded-xl border-2 border-dashed transition-colors",
                                                        snapshot.isDraggingOver
                                                            ? "border-violet-300 dark:border-violet-600"
                                                            : "border-neutral-200 dark:border-neutral-800",
                                                        releaseGroup.tasks.length === 0 && releaseGroup.stats?.activeCount === 0 && !snapshot.isDraggingOver && "flex items-center justify-center"
                                                    )}
                                                >
                                                    {releaseGroup.tasks.length === 0 && releaseGroup.stats?.activeCount === 0 && !snapshot.isDraggingOver ? (
                                                        <div className="w-full flex items-center justify-center py-8 px-4">
                                                            <QuickAddTask
                                                                status="todo"
                                                                scope="backlog"
                                                                projectId={project.id}
                                                                organizationSlug={project.organizationSlug}
                                                                assigneeId="unassigned"
                                                                onTaskCreated={handleTaskCreated}
                                                                alwaysShow={true}
                                                                releaseId={releaseId === 'no-release' ? null : releaseId}
                                                                buttonText={releaseId === 'no-release' ? "Create task in Backlog" : `Create task in ${releaseGroup.release.name}`}
                                                                textSize="text-xs"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className={cn("space-y-2", (releaseGroup.tasks.length > 0 || releaseGroup.stats?.activeCount > 0) && "p-2")}>
                                                            {(() => {
                                                                // Get active tasks for this release to show as placeholders
                                                                const activeTasks = activeWeekTasks.filter(task => 
                                                                    task.release?.id === releaseId
                                                                );
                                                                
                                                                // Combine active placeholders and backlog tasks
                                                                const allItems = [];
                                                                
                                                                // Add active task placeholders first
                                                                activeTasks.forEach((task) => {
                                                                    allItems.push({
                                                                        type: 'placeholder',
                                                                        task,
                                                                        key: `active-${task.id}`
                                                                    });
                                                                });
                                                                
                                                                // Add backlog tasks
                                                                releaseGroup.tasks.forEach((task, index) => {
                                                                    allItems.push({
                                                                        type: 'task',
                                                                        task,
                                                                        index,
                                                                        key: task.id
                                                                    });
                                                                });
                                                                
                                                                return allItems.map((item) => {
                                                                    if (item.type === 'placeholder') {
                                                                        return (
                                                                            <TaskPlaceholderItem
                                                                                key={item.key}
                                                                                task={item.task}
                                                                                statuses={project?.statuses || []}
                                                                                onGoToTask={(task) => {
                                                                                    // Scroll to the task in active week
                                                                                    const taskElement = document.querySelector(`[data-task-id="${task.id}"]`);
                                                                                    if (taskElement) {
                                                                                        taskElement.scrollIntoView({
                                                                                            behavior: 'smooth',
                                                                                            block: 'center'
                                                                                        });
                                                                                        // Add a brief white highlight effect
                                                                                        taskElement.classList.add('ring-2', 'ring-white', 'ring-offset-2');
                                                                                        setTimeout(() => {
                                                                                            taskElement.classList.remove('ring-2', 'ring-white', 'ring-offset-2');
                                                                                        }, 2000);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <Draggable
                                                                                key={item.key}
                                                                                draggableId={item.task.id}
                                                                                index={item.index}
                                                                                isDragDisabled={selectedTasks.size > 0}
                                                                            >
                                                                                {(provided, snapshot) => (
                                                                                    <div
                                                                                        ref={provided.innerRef}
                                                                                        {...provided.draggableProps}
                                                                                        {...provided.dragHandleProps}
                                                                                    >
                                                                                        <TaskListItem
                                                                                            task={item.task}
                                                                                            organizationSlug={project.organizationSlug}
                                                                                            onTaskUpdate={handleTaskUpdate}
                                                                                            onSelectionChange={handleTaskSelection}
                                                                                            isSelected={selectedTasks.has(item.task.id)}
                                                                                            isDragging={snapshot.isDragging}
                                                                                            isDragDisabled={selectedTasks.size > 0}
                                                                                            data-task-id={item.task.id}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </Draggable>
                                                                        );
                                                                    }
                                                                });
                                                            })()}
                                                            
                                                            {(releaseGroup.tasks.length > 0 || releaseGroup.stats?.activeCount > 0) && (
                                                                <QuickAddTask
                                                                    status="todo"
                                                                    scope="backlog"
                                                                    projectId={project.id}
                                                                    organizationSlug={project.organizationSlug}
                                                                    assigneeId="unassigned"
                                                                    onTaskCreated={handleTaskCreated}
                                                                    alwaysShow={true}
                                                                    releaseId={releaseId === 'no-release' ? null : releaseId}
                                                                    buttonText={releaseId === 'no-release' ? "Create task in Backlog" : `Create task in ${releaseGroup.release.name}`}
                                                                    textSize="text-xs"
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                ))}
                                

                                
                                {/* No Release Assigned section */}
                                {backlogReleaseGroups['no-release'] && (
                                    <div className="space-y-3">
                                        {/* Release Header */}
                                        <div className="flex items-center gap-3 py-2">
                                            <span className="font-semibold text-neutral-900 dark:text-neutral-100">{backlogReleaseGroups['no-release'].release.name}</span>
                                            <Badge variant="secondary" className="text-xs bg-neutral-50 text-neutral-600 dark:bg-neutral-900/30 dark:text-neutral-400 border-neutral-200 dark:border-neutral-700">
                                                Unassigned
                                            </Badge>
                                            
                                            {/* Task counts and active status */}
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <span>
                                                    {backlogReleaseGroups['no-release'].tasks.length} backlog
                                                </span>
                                                
                                                {backlogReleaseGroups['no-release'].stats && backlogReleaseGroups['no-release'].stats.activeCount > 0 && (
                                                    <>
                                                        <span className="text-neutral-300 dark:text-neutral-700">•</span>
                                                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                                            {backlogReleaseGroups['no-release'].stats.activeCount} active
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>


                                        
                                        {/* Tasks Drop Zone */}
                                        <Droppable droppableId="backlog">
                                            {(provided, snapshot) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    className={cn(
                                                        "space-y-2 min-h-[60px] rounded-xl border-2 border-dashed transition-colors",
                                                        snapshot.isDraggingOver
                                                            ? "border-violet-300 dark:border-violet-600"
                                                            : "border-neutral-200 dark:border-neutral-800",
                                                        backlogReleaseGroups['no-release'].tasks.length === 0 && backlogReleaseGroups['no-release'].stats?.activeCount === 0 && !snapshot.isDraggingOver && "flex items-center justify-center"
                                                    )}
                                                >
                                                    {backlogReleaseGroups['no-release'].tasks.length === 0 && backlogReleaseGroups['no-release'].stats?.activeCount === 0 && !snapshot.isDraggingOver ? (
                                                        <div className="w-full flex items-center justify-center py-8 px-4">
                                                            <QuickAddTask
                                                                status="todo"
                                                                scope="backlog"
                                                                projectId={project.id}
                                                                organizationSlug={project.organizationSlug}
                                                                assigneeId="unassigned"
                                                                onTaskCreated={handleTaskCreated}
                                                                alwaysShow={true}
                                                                releaseId={null}
                                                                buttonText="Create task in Backlog"
                                                                textSize="text-xs"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className={cn("space-y-2", (backlogReleaseGroups['no-release'].tasks.length > 0 || backlogReleaseGroups['no-release'].stats?.activeCount > 0) && "p-2")}>
                                                            {backlogReleaseGroups['no-release'].tasks.map((task, index) => (
                                                                <Draggable
                                                                    key={task.id}
                                                                    draggableId={task.id}
                                                                    index={index}
                                                                    isDragDisabled={selectedTasks.size > 0}
                                                                >
                                                                    {(provided, snapshot) => (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                        >
                                                                            <TaskListItem
                                                                                task={task}
                                                                                organizationSlug={project.organizationSlug}
                                                                                onTaskUpdate={handleTaskUpdate}
                                                                                onSelectionChange={handleTaskSelection}
                                                                                isSelected={selectedTasks.has(task.id)}
                                                                                isDragging={snapshot.isDragging}
                                                                                isDragDisabled={selectedTasks.size > 0}
                                                                                data-task-id={task.id}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}

                                                            <QuickAddTask
                                                                status="todo"
                                                                scope="backlog"
                                                                projectId={project.id}
                                                                organizationSlug={project.organizationSlug}
                                                                assigneeId="unassigned"
                                                                onTaskCreated={handleTaskCreated}
                                                                alwaysShow={true}
                                                                releaseId={null}
                                                                buttonText="Create task in Backlog"
                                                                textSize="text-xs"
                                                            />
                                                        </div>
                                                    )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </DragDropContext>
            
            {/* Floating Selection Bar */}
            {selectedTasks.size > 0 && (
                <div className="fixed bottom-8 left-10 z-50 bg-gradient-to-r from-violet-700 to-purple-800 dark:from-violet-800 dark:to-purple-900 text-white shadow-2xl rounded-2xl backdrop-blur-lg">
                    <div className="px-6 py-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                    <span className="font-bold text-lg">{selectedTasks.size}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="font-semibold text-sm">
                                        {selectedTasks.size} task{selectedTasks.size !== 1 ? 's' : ''} selected
                                    </span>
                                    <span className="text-xs text-white/70">
                                        Bulk actions only • Drag disabled
                                    </span>
                                </div>
                            </div>
                            
                            <div className="h-8 w-px bg-white/30" />
                            
                            <div className="flex items-center gap-3">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className="bg-white/20 hover:bg-white/30 text-white border-white/30 backdrop-blur-sm h-10 px-4"
                                        >
                                            <MoveVertical className="w-4 h-4 mr-2" />
                                            Move to
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="w-auto min-w-[200px] max-w-[320px] mb-2">
                                        {/* Active Week Section */}
                                        {activeReleases.length > 0 ? (
                                            <>
                                                <DropdownMenuItem
                                                    onClick={() => {
                                                        handleBulkMoveToTarget('active');
                                                    }}
                                                    className="flex items-center gap-2 whitespace-nowrap"
                                                >
                                                    <Zap className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                                    <span>Active Week</span>
                                                    <span className="text-xs text-muted-foreground">(No Release)</span>
                                                </DropdownMenuItem>
                                                
                                                <div className="pl-4 border-l-2 border-emerald-200 dark:border-emerald-800 ml-4 my-1 space-y-0.5">
                                                    {activeReleases.map((release) => {
                                                        const colors = getReleaseStatusColor(release.status);
                                                        return (
                                                            <DropdownMenuItem
                                                                key={release._id}
                                                                onClick={() => {
                                                                    handleBulkMoveToTarget(`active-release-${release._id}`);
                                                                }}
                                                                className="flex items-center gap-2 text-sm whitespace-nowrap pr-2"
                                                            >
                                                                <FiPackage className={cn("w-3.5 h-3.5 flex-shrink-0", colors.icon)} />
                                                                <span className="truncate">{release.name}</span>
                                                                <span className="text-xs text-muted-foreground">{release.targetDate ? format(new Date(release.targetDate), 'MMM d') : ''}</span>
                                                            </DropdownMenuItem>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        ) : (
                                            <DropdownMenuItem
                                                onClick={() => {
                                                    handleBulkMoveToTarget('active');
                                                }}
                                                className="flex items-center gap-2 whitespace-nowrap"
                                            >
                                                <Zap className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                                <span>Active Week</span>
                                            </DropdownMenuItem>
                                        )}
                                        
                                        <DropdownMenuSeparator className="my-2" />
                                        
                                        {/* Backlog Section */}
                                        {Object.entries(backlogReleaseGroups).filter(([id]) => id !== 'no-release').length > 0 ? (
                                            <>
                                                <DropdownMenuItem
                                                    onClick={() => {
                                                        handleBulkMoveToTarget('backlog');
                                                    }}
                                                    className="flex items-center gap-2 whitespace-nowrap"
                                                >
                                                    <Archive className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                                                    <span>Backlog</span>
                                                    <span className="text-xs text-muted-foreground">(No Release)</span>
                                                </DropdownMenuItem>
                                                
                                                <div className="pl-4 border-l-2 border-neutral-200 dark:border-neutral-800 ml-4 my-1 space-y-0.5">
                                                    {Object.entries(backlogReleaseGroups)
                                                        .filter(([id]) => id !== 'no-release')
                                                        .map(([id, group]) => (
                                                            <DropdownMenuItem
                                                                key={id}
                                                                onClick={() => {
                                                                    handleBulkMoveToTarget(id);
                                                                }}
                                                                className="flex items-center gap-2 text-sm whitespace-nowrap pr-2"
                                                            >
                                                                <FiPackage className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
                                                                <span className="truncate">{group.release.name}</span>
                                                                {group.release.targetDate && (
                                                                    <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                                                                        {format(new Date(group.release.targetDate), 'MMM d')}
                                                                    </span>
                                                                )}
                                                            </DropdownMenuItem>
                                                        ))
                                                    }
                                                </div>
                                            </>
                                        ) : (
                                            <DropdownMenuItem
                                                onClick={() => {
                                                    handleBulkMoveToTarget('backlog');
                                                }}
                                                className="flex items-center gap-2 whitespace-nowrap"
                                            >
                                                <Archive className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                                                <span>Backlog</span>
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-white hover:bg-white/20 hover:text-white h-10 w-10 p-0"
                                    onClick={() => setSelectedTasks(new Set())}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Release Activation Dialog */}
            <Dialog open={showReleaseActivationDialog} onOpenChange={setShowReleaseActivationDialog}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        Move to Active Week
                    </DialogTitle>
                    <DialogDescription className="space-y-3">
                        <div className="text-sm">
                            Moving {pendingDragOperation?.tasksToMove?.length || 1} {(pendingDragOperation?.tasksToMove?.length || 1) === 1 ? 'task' : 'tasks'} from <strong>"{pendingDragOperation?.releaseName}"</strong> to the active week.
                        </div>
                    </DialogDescription>
                </DialogHeader>
                
                <RadioGroup value={activationChoice} onValueChange={setActivationChoice}>
                    <Label 
                        htmlFor="move-only" 
                        className={cn(
                            "flex items-start space-x-4 p-4 rounded-lg border transition-all duration-200 cursor-pointer",
                            activationChoice === "move-task-only"
                                ? "border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20"
                                : "border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-950/50 hover:bg-neutral-100 dark:hover:bg-neutral-900/50"
                        )}
                    >
                        <RadioGroupItem value="move-task-only" id="move-only" className="mt-1" />
                        <div className="flex-1 space-y-1">
                            <div className="font-medium">
                                Move {(pendingDragOperation?.tasksToMove?.length || 1) === 1 ? 'task' : 'tasks'} to active week
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Keep {(pendingDragOperation?.tasksToMove?.length || 1) === 1 ? 'it' : 'them'} assigned to this release
                            </div>
                        </div>
                    </Label>
                    
                    <Label 
                        htmlFor="activate" 
                        className={cn(
                            "flex items-start space-x-4 p-4 rounded-lg border transition-all duration-200 cursor-pointer",
                            activationChoice === "activate-release"
                                ? "border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20"
                                : "border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-950/50 hover:bg-neutral-100 dark:hover:bg-neutral-900/50"
                        )}
                    >
                        <RadioGroupItem value="activate-release" id="activate" className="mt-1" />
                        <div className="flex-1 space-y-1">
                            <div className="font-medium">
                                Activate entire release
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Move all {backlogReleaseGroups[pendingDragOperation?.sourceReleaseId]?.tasks?.length || 0} tasks from this release to active week
                            </div>
                        </div>
                    </Label>
                </RadioGroup>
                
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setShowReleaseActivationDialog(false);
                            setPendingDragOperation(null);
                            setActivationChoice("move-task-only");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            if (pendingDragOperation) {
                                // Close dialog immediately for optimistic update
                                setShowReleaseActivationDialog(false);
                                setPendingDragOperation(null);
                                setActivationChoice("move-task-only");
                                
                                if (activationChoice === 'activate-release') {
                                    // Handle bulk operation differently
                                    if (pendingDragOperation.isBulkOperation) {
                                        // Activate the entire release
                                        await executeDragOperation(
                                            null,
                                            null,
                                            [],
                                            { 
                                                activateRelease: true, 
                                                releaseId: pendingDragOperation.sourceReleaseId 
                                            }
                                        );
                                    } else {
                                        await executeDragOperation(
                                            pendingDragOperation.source,
                                            pendingDragOperation.destination,
                                            pendingDragOperation.tasksToMove,
                                            { 
                                                activateRelease: true, 
                                                releaseId: pendingDragOperation.sourceReleaseId 
                                            }
                                        );
                                    }
                                } else {
                                    // For move task only (keep release assignment)
                                    if (pendingDragOperation.isBulkOperation) {
                                        // Set bulk move target and perform bulk move
                                        setBulkMoveTarget('active');
                                        await performBulkMove(pendingDragOperation.tasksToMove, 'active', { keepRelease: true });
                                    } else {
                                        performOptimisticUpdate(
                                            pendingDragOperation.source,
                                            pendingDragOperation.destination,
                                            pendingDragOperation.tasksToMove,
                                            { keepRelease: true }
                                        );
                                        await executeDragOperation(
                                            pendingDragOperation.source,
                                            pendingDragOperation.destination,
                                            pendingDragOperation.tasksToMove,
                                            { keepRelease: true }
                                        );
                                    }
                                }
                            }
                        }}
                    >
                        Confirm
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Release Menu Activation Dialog */}
        <Dialog open={showReleaseMenuActivationDialog} onOpenChange={setShowReleaseMenuActivationDialog}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        Activate Release
                    </DialogTitle>
                    <DialogDescription className="space-y-3 pt-3">
                        <p>
                            Are you sure you want to activate <strong>"{releaseToActivate?.name}"</strong>?
                        </p>
                        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                                This will move all {backlogReleaseGroups[releaseToActivate?._id]?.tasks.length || 0} tasks from this release to the active week and mark the release as started.
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>
                
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setShowReleaseMenuActivationDialog(false);
                            setReleaseToActivate(null);
                        }}
                        disabled={isActivatingRelease}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmActivateRelease}
                        disabled={isActivatingRelease}
                    >
                        {isActivatingRelease ? 'Activating...' : 'Activate Release'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Release Deactivation Dialog */}
        <Dialog open={showReleaseDeactivationDialog} onOpenChange={setShowReleaseDeactivationDialog}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Archive className="h-5 w-5 text-neutral-500" />
                        Move Release to Backlog
                    </DialogTitle>
                    <DialogDescription className="space-y-3 pt-3">
                        <p>
                            Are you sure you want to move <strong>"{releaseToDeactivate?.name}"</strong> back to the backlog?
                        </p>
                        <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                This will move all {activeWeekTasks.filter(task => task.release?.id === releaseToDeactivate?._id).length || 0} active tasks from this release back to the backlog.
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>
                
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setShowReleaseDeactivationDialog(false);
                            setReleaseToDeactivate(null);
                        }}
                        disabled={isDeactivatingRelease}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDeactivateRelease}
                        disabled={isDeactivatingRelease}
                    >
                        {isDeactivatingRelease ? 'Moving...' : 'Move to Backlog'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Create New Release Dialog */}
        <Dialog open={showNewReleaseForm} onOpenChange={(open) => {
            setShowNewReleaseForm(open);
            if (!open) {
                setNewReleaseName("");
                setNewReleaseTargetDate(null);
                setDatePickerOpen(false);
            }
        }}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FiPackage className="h-5 w-5 text-neutral-600" />
                        Create New Release
                    </DialogTitle>
                    <DialogDescription>
                        Add a new release to organize your backlog tasks.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="release-name">Release name</Label>
                        <Input
                            id="release-name"
                            value={newReleaseName}
                            onChange={(e) => setNewReleaseName(e.target.value)}
                            placeholder="e.g., v1.0.0, Sprint 23, Q1 Release"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreateRelease();
                                }
                            }}
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Target date (optional)</Label>
                        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "w-full justify-start text-left font-normal",
                                        !newReleaseTargetDate && "text-muted-foreground"
                                    )}
                                >
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {newReleaseTargetDate ? format(newReleaseTargetDate, "PPP") : "Select target date"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                    mode="single"
                                    selected={newReleaseTargetDate}
                                    onSelect={(date) => {
                                        setNewReleaseTargetDate(date);
                                        setDatePickerOpen(false);
                                    }}
                                    initialFocus
                                />
                                {newReleaseTargetDate && (
                                    <div className="p-2 border-t">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full"
                                            onClick={() => {
                                                setNewReleaseTargetDate(null);
                                                setDatePickerOpen(false);
                                            }}
                                        >
                                            Clear date
                                        </Button>
                                    </div>
                                )}
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
                
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => setShowNewReleaseForm(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateRelease}
                        disabled={!newReleaseName.trim()}
                    >
                        Create Release
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>




        {/* Release Delete Dialog */}
        <Dialog open={showReleaseDeleteDialog} onOpenChange={setShowReleaseDeleteDialog}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                        Delete Release
                    </DialogTitle>
                    <DialogDescription className="space-y-3 pt-3">
                        <p>
                            Are you sure you want to delete <strong>"{releaseToDelete?.name}"</strong>?
                        </p>
                        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                            <p className="text-sm text-red-800 dark:text-red-200">
                                This action cannot be undone. All tasks assigned to this release will be removed from the release.
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>
                
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setShowReleaseDeleteDialog(false);
                            setReleaseToDelete(null);
                        }}
                        disabled={isDeletingRelease}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={confirmDeleteRelease}
                        disabled={isDeletingRelease}
                    >
                        {isDeletingRelease ? "Deleting..." : "Delete Release"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Release Ship Confirmation Dialog */}
        <Dialog open={showReleaseShipDialog} onOpenChange={setShowReleaseShipDialog}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                        Ship Release
                    </DialogTitle>
                    <DialogDescription className="space-y-3 pt-3">
                        <p>
                            Are you sure you want to mark <strong>"{releaseToShip?.name}"</strong> as shipped?
                        </p>
                        <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                            <p className="text-sm text-green-800 dark:text-green-200">
                                This will set the release date to today and mark the release as completed.
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>
                
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setShowReleaseShipDialog(false);
                            setReleaseToShip(null);
                        }}
                        disabled={isShippingRelease}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmShipRelease}
                        disabled={isShippingRelease}
                    >
                        {isShippingRelease ? "Shipping..." : "Ship Release"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}
